/**
 * Faction 配置块 JSON Schema 定义
 */
export const factionSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/faction.json',
  title: 'Faction 配置',
  description: 'Faction（派系）配置数据结构',
  type: 'object',
  required: ['faction_id', 'name'],
  properties: {
    faction_id: {
      type: 'string',
      title: 'Faction ID',
      description: 'Faction 唯一标识符',
      pattern: '^faction_[a-zA-Z0-9_]+$',
      minLength: 9,
      maxLength: 50,
      examples: ['faction_resistance', 'faction_empire', 'faction_merchants'],
    },
    name: {
      type: 'string',
      title: '派系名称',
      description: '派系的显示名称',
      minLength: 1,
      maxLength: 100,
      examples: ['抵抗军', '帝国', '商人联盟'],
    },
    description: {
      type: 'string',
      title: '派系描述',
      description: '派系的详细描述',
      minLength: 0,
      maxLength: 1000,
    },
    ideology: {
      type: 'string',
      title: '意识形态',
      description: '派系的核心理念',
      maxLength: 500,
      examples: ['自由与抵抗', '秩序与统治', '财富与利益'],
    },
    leader: {
      type: 'string',
      title: '领袖',
      description: '派系领袖的 NPC ID',
      pattern: '^npc_[a-zA-Z0-9_]+$',
    },
    members: {
      type: 'array',
      title: '成员列表',
      description: '派系成员的 NPC ID 列表',
      items: {
        type: 'string',
        pattern: '^npc_[a-zA-Z0-9_]+$',
      },
    },
    goals: {
      type: 'array',
      title: '目标',
      description: '派系的主要目标',
      items: {
        type: 'object',
        required: ['goal_id', 'description'],
        properties: {
          goal_id: {
            type: 'string',
            title: '目标 ID',
          },
          description: {
            type: 'string',
            title: '目标描述',
            maxLength: 200,
          },
          priority: {
            type: 'integer',
            title: '优先级',
            minimum: 0,
            maximum: 100,
            default: 50,
          },
          status: {
            type: 'string',
            title: '状态',
            enum: ['未开始', '进行中', '已完成', '失败'],
            default: '未开始',
          },
        },
      },
    },
    relationships: {
      type: 'object',
      title: '派系关系',
      description: '与其他派系的关系',
      additionalProperties: {
        type: 'object',
        required: ['faction_id', 'relationship_type'],
        properties: {
          faction_id: {
            type: 'string',
            pattern: '^faction_[a-zA-Z0-9_]+$',
          },
          relationship_type: {
            type: 'string',
            enum: ['同盟', '敌对', '中立', '竞争', '附庸'],
          },
          intimacy: {
            type: 'integer',
            title: '亲密度',
            minimum: -100,
            maximum: 100,
            default: 0,
          },
          description: {
            type: 'string',
            title: '关系描述',
            maxLength: 200,
          },
        },
      },
    },
    reputation_levels: {
      type: 'array',
      title: '声望等级',
      description: '玩家在派系中的声望等级划分',
      items: {
        type: 'object',
        required: ['level', 'name', 'min_reputation'],
        properties: {
          level: {
            type: 'integer',
            title: '等级',
            minimum: 1,
          },
          name: {
            type: 'string',
            title: '等级名称',
            maxLength: 50,
            examples: ['陌生人', '熟人', '朋友', '核心成员', '领导层'],
          },
          min_reputation: {
            type: 'integer',
            title: '最低声望值',
            description: '达到此等级所需的最低声望',
          },
          benefits: {
            type: 'array',
            title: '福利',
            description: '该等级享有的福利',
            items: {
              type: 'string',
              maxLength: 100,
            },
          },
        },
      },
    },
    territory: {
      type: 'array',
      title: '势力范围',
      description: '派系控制的地点',
      items: {
        type: 'string',
        pattern: '^location_[a-zA-Z0-9_]+$',
      },
    },
    resources: {
      type: 'object',
      title: '资源',
      description: '派系拥有的资源',
      properties: {
        military_power: {
          type: 'integer',
          title: '军事力量',
          minimum: 0,
        },
        economic_power: {
          type: 'integer',
          title: '经济实力',
          minimum: 0,
        },
        influence: {
          type: 'integer',
          title: '影响力',
          minimum: 0,
        },
      },
    },
    emblem: {
      type: 'string',
      title: '徽记',
      description: '派系徽记的资源路径或 URL',
      maxLength: 200,
    },
    color: {
      type: 'string',
      title: '代表色',
      description: '派系的代表颜色（HEX 格式）',
      pattern: '^#[0-9A-Fa-f]{6}$',
      examples: ['#FF0000', '#00FF00', '#0000FF'],
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type FactionConfig = {
  faction_id: string
  name: string
  description?: string
  ideology?: string
  leader?: string
  members?: string[]
  goals?: Array<{
    goal_id: string
    description: string
    priority?: number
    status?: string
  }>
  relationships?: Record<
    string,
    {
      faction_id: string
      relationship_type: string
      intimacy?: number
      description?: string
    }
  >
  reputation_levels?: Array<{
    level: number
    name: string
    min_reputation: number
    benefits?: string[]
  }>
  territory?: string[]
  resources?: {
    military_power?: number
    economic_power?: number
    influence?: number
  }
  emblem?: string
  color?: string
  metadata?: Record<string, any>
}
